/**
 * Dynamic Language Loader
 *
 * Handles dynamic loading and activation of language message files
 * for the Lingui i18n system.
 */

import type { Messages } from "@lingui/core";
import { i18n } from "@lingui/core";
import { captureException } from "@/utils/error-tracking";
import {
	SUPPORTED_LANGUAGES,
	type SupportedLanguage,
} from "./language-detection";

/**
 * Cache for loaded message catalogs to avoid re-importing
 */
const messageCache = new Map<SupportedLanguage, Messages>();

/**
 * Tracks the currently active language
 */
let currentLanguage: SupportedLanguage = "en";

/**
 * Dynamically loads and activates a language
 *
 * @param locale - The language code to load ('en' or 'es')
 * @returns Promise that resolves to the activated language code
 */
export async function loadLanguage(
	locale: SupportedLanguage,
): Promise<SupportedLanguage> {
	try {
		// Check cache first
		let messages = messageCache.get(locale);

		if (!messages) {
			// Dynamically import the message catalog
			messages = await importMessages(locale);

			// Cache the messages for future use
			if (messages) {
				messageCache.set(locale, messages);
			}
		}

		if (messages) {
			// Load and activate the language in Lingui
			i18n.load(locale, messages);
			i18n.activate(locale);
			currentLanguage = locale;

			return locale;
		} else {
			throw new Error(`Failed to load messages for locale: ${locale}`);
		}
	} catch (error) {
		captureException(error, {
			tags: { component: "i18n-loader", action: "loadLanguage" },
			extra: { locale },
		});
		console.error(`Error loading language ${locale}:`, error);

		// Fallback to English if the requested language fails
		if (locale !== "en") {
			return await loadLanguage("en");
		}

		// If English also fails, we have a serious problem
		throw new Error("Failed to load any language, including English fallback");
	}
}

/**
 * Dynamically imports message files based on locale
 *
 * @param locale - The language code to import
 * @returns Promise that resolves to the Messages object
 */
async function importMessages(
	locale: SupportedLanguage,
): Promise<Messages | undefined> {
	try {
		switch (locale) {
			case "en": {
				const { messages } = await import("../locales/en/messages.ts");
				return messages;
			}
			case "es": {
				const { messages } = await import("../locales/es/messages.ts");
				return messages;
			}
			case "fr": {
				const { messages } = await import("../locales/fr/messages.ts");
				return messages;
			}
			case "pt": {
				const { messages } = await import("../locales/pt/messages.ts");
				return messages;
			}
			case "uk": {
				const { messages } = await import("../locales/uk/messages.ts");
				return messages;
			}
			default: {
				console.warn(`Unsupported locale: ${locale}`);
				return undefined;
			}
		}
	} catch (error) {
		captureException(error, {
			tags: { component: "i18n-loader", action: "importMessages" },
			extra: { locale },
		});
		console.error(`Failed to import messages for locale ${locale}:`, error);
		return undefined;
	}
}

/**
 * Gets the currently active language
 *
 * @returns The currently active language code
 */
export function getCurrentLanguage(): SupportedLanguage {
	return currentLanguage;
}

/**
 * Preloads all available languages for better performance
 * This can be called during app initialization to cache all message catalogs
 *
 * @returns Promise that resolves when all languages are preloaded
 */
export async function preloadAllLanguages(): Promise<void> {
	const languages: readonly SupportedLanguage[] = SUPPORTED_LANGUAGES;

	try {
		await Promise.all(
			languages.map(async (lang) => {
				if (!messageCache.has(lang)) {
					const messages = await importMessages(lang);
					if (messages) {
						messageCache.set(lang, messages);
					}
				}
			}),
		);
	} catch (error) {
		captureException(error, {
			tags: { component: "i18n-loader", action: "preloadAllLanguages" },
		});
		console.warn("Some languages failed to preload:", error);
	}
}

/**
 * Switches to a different language dynamically
 * This can be used for runtime language switching
 *
 * @param locale - The language to switch to
 * @returns Promise that resolves to the activated language
 */
export async function switchLanguage(
	locale: SupportedLanguage,
): Promise<SupportedLanguage> {
	if (locale === currentLanguage) {
		return locale;
	}

	return await loadLanguage(locale);
}

/**
 * Initializes the i18n system with a specific language
 * This should be called once during app bootstrap
 *
 * @param locale - The initial language to load
 * @param preloadOthers - Whether to preload other languages in the background
 * @returns Promise that resolves to the initialized language
 */
export async function initializeI18n(
	locale: SupportedLanguage,
	preloadOthers: boolean = false,
): Promise<SupportedLanguage> {
	// Load the primary language first
	const activatedLanguage = await loadLanguage(locale);

	// Optionally preload other languages in the background
	if (preloadOthers) {
		// Don't await this - let it happen in the background
		preloadAllLanguages().catch((error) => {
			captureException(error, {
				tags: {
					component: "i18n-loader",
					action: "preloadAllLanguagesBackground",
				},
			});
			console.warn("Background preloading failed:", error);
		});
	}

	return activatedLanguage;
}

/**
 * Clears the message cache
 * Useful for testing or if you need to force reload messages
 */
export function clearMessageCache(): void {
	messageCache.clear();
}
